import {
	SceneObject,
	ConnectionManager,
	GridTools,
	ConnectionRelation,
	Groups,
	ModelData,
	ProductData,
	TransformVector,
	ConnectableObject,
	View3dElement,
	GridBox,
} from '@ikea-aoa/component-view-3d';
import { ModuleConnectionManager } from '../../util/SceneUtils/ModuleConnectionManager';
import { ConnectionKitsManager } from '../../util/SceneUtils/ConnectionKitsManager';
import { ObjectManager } from './ObjectManager';
import { Connection, SnappingGrid, SnappingPoint } from '../../util/SceneUtils/SnappingGrid';
import { SmartMirrorManager } from '../../util/SceneUtils/SmartMirrorManager';

import {
	CoverItem,
	Platform,
	RangeSettings,
	SofaConfiguration,
	SofaConfigurationItem,
	IModuleSwiperItem,
} from 'sofa-reborn-react';

import { TransformPosition, TransformRotation } from '@ikea-aoa/elements-3d';
import { Product } from '@ikea-aoa/component-view-3d';
import { FamilyType } from '@ikea-aoa/component-view-3d/lib/main/connection-manager';
import { ProductUtils } from '../../util/ProductUtils';

type Props = {
	moduleItems: IModuleSwiperItem[];
	rangeSettings: RangeSettings;
	sofaGroups: Groups;
	products: ProductData;
	sofaModels: ModelData;
	assetFolder: string | null;
	loadingModels: boolean;
	cover: CoverItem;
	selectedArmrest: Product | null;
	platform: Platform;
	showDebug?: boolean | undefined;
	specificItems: Map<string, SceneObject>;
};

export class SceneLogic {
	public connectionManager: ConnectionManager;
	public moduleConnectionManager: ModuleConnectionManager;
	public connectionKitManager: ConnectionKitsManager;
	public smartMirrorManager: SmartMirrorManager;

	public sceneItems: Map<string, SceneObject>;
	public objectManager: ObjectManager;
	public snappingGrid: SnappingGrid;

	private gridTools: GridTools;

	constructor(private view3d: View3dElement, private props: Props) {
		this.sceneItems = new Map<string, SceneObject>();
		this.InitiateManagers();
	}

	/**
	 * Update props.
	 *
	 * @param props Props.
	 */
	public updateProps(props: Props): void {
		this.props = props;
		const { sofaGroups, products, sofaModels } = this.props;
		this.connectionManager.setRules(products, sofaGroups, sofaModels);
		this.objectManager.updateProps(props);
	}

	/**
	 * Check if sofa is modular.
	 *
	 */
	public isModularSofa(): boolean {
		if (this.props.rangeSettings.onlyRemoveOuterModules !== undefined) {
			return !this.props.rangeSettings.onlyRemoveOuterModules;
		}
		return false;
	}

	/**
	 * Check if there is only one module on scene.
	 *
	 */
	public oneModuleOnScene(): boolean {
		const modules = new Array<SceneObject>();
		this.sceneItems.forEach((item) => {
			if (!item.parentId) {
				modules.push(item);
			}
		});
		return modules.length == 1;
	}

	/**
	 * Check if article has extended logic.
	 *
	 * @param articleId Article ID.
	 */
	public isExtended(articleId: string): boolean {
		const model = this.connectionManager.getModelData(articleId);
		if (!model) {
			throw new Error('No model data!');
		}
		return model.bounds['extended'] ? true : false;
	}

	/**
	 * Check if scene object is a middle section now.
	 *
	 * @param sceneObject Scene Object.
	 */
	public isMiddleSection(sceneObject: SceneObject): boolean {
		const connectedModules = this.moduleConnectionManager.getAllConnectedModules(sceneObject.id);
		if (!this.isModularSofa() && this.connectionManager.isAddAsChild(sceneObject.articleId)) {
			if (connectedModules.length > 0) {
				if (connectedModules.length === 1 && !sceneObject.parentId) {
					return false;
				} else {
					return true;
				}
			}
			return false;
		}
		if (connectedModules && connectedModules.length > 1) {
			const shouldExcludeFromMiddleDrag = this.isAllModulesMiddleModuleDrag();
			if (
				this.props.rangeSettings.connectionsToExcludeFromMiddleDrag &&
				shouldExcludeFromMiddleDrag
			) {
				const excludeFromMiddleDrag = this.props.rangeSettings.connectionsToExcludeFromMiddleDrag;
				const connIncludesExcludedConn = connectedModules.some((module) => {
					if (
						excludeFromMiddleDrag.includes(module.connection.from) ||
						excludeFromMiddleDrag.includes(module.connection.to)
					) {
						return (
							this.moduleConnectionManager.getAllConnectedModules(module.object.id).length === 2
						);
					}
					return false;
				});

				if (connIncludesExcludedConn) {
					return false;
				}
			}
			return true;
		}
		return false;
	}

	/**
	 * Check if seat at other end.
	 *
	 * @param sceneObject Scene Object.
	 */
	public backrestAsArmrest(sceneObject: SceneObject): boolean {
		let isBackrestMovable = false;
		const model = this.connectionManager.getModelData(sceneObject.articleId);
		if (
			this.props.rangeSettings.rangeName === 'JÄTTEBO' &&
			model &&
			model.modelId === 'JATTEBO_1_5_SEAT'
		) {
			const sceneObjectId = sceneObject.id;
			const sceneObjectArticleId = sceneObject.articleId;
			const sceneItemsArr = Array.from(this.sceneItems.values());
			const hasArmrest = sceneItemsArr.some(
				(sceneItem) => this.connectionManager.getFamily(sceneItem.articleId) === 'ARMREST'
			);
			if (!hasArmrest) {
				this.sceneItems.forEach((item) => {
					if (!item.articleId.includes('backrest')) {
						const modelConnections = this.moduleConnectionManager.getAllConnectedModules(item.id);
						const itemConnections =
							this.moduleConnectionManager.getAllConnectedModules(sceneObjectId);
						const itemOccupied = this.connectionManager.getOccupiedConnections(
							sceneObjectId,
							sceneObjectArticleId
						);

						const occupiedConnectionsOnItem = [
							...itemOccupied,
							...itemConnections.map((conn) => {
								return conn.connection.from;
							}),
						];
						const leftAndRightOccupied =
							occupiedConnectionsOnItem.includes('LeftClamp') &&
							occupiedConnectionsOnItem.includes('RightClamp');
						if (modelConnections.length < 2 && sceneObjectId !== item.id && !leftAndRightOccupied) {
							const family = this.connectionManager.getFamily(item.articleId);
							if (family === 'SEAT') {
								isBackrestMovable = true;
							}
						}
					}
				});
			}
		}
		return isBackrestMovable;
	}

	/**
	 * Check if scene object can be disconnected.
	 *
	 * @param sceneObject Scene Object.
	 */
	public isDisconnectable(sceneObject: SceneObject): boolean {
		if (this.connectionManager.isAutoConnectMovable(sceneObject)) {
			return true;
		}
		if (this.connectionManager.isAutoConnectableModel(sceneObject.articleId)) {
			return false;
		}

		return this.isModularSofa() || !this.isMiddleSection(sceneObject);
	}

	/**
	 * Check if scene object can be rotated.
	 *
	 * @param sceneObject Scene Object.
	 */
	public isRotatable(sceneObject: SceneObject): boolean {
		const { parentId } = sceneObject;
		if (this.props.rangeSettings.modularRotation) {
			if (!this.connectionManager.isSeparate(sceneObject.articleId)) {
				// Can't rotate items that can not be placed separate
				return false;
			}
			const newRotation = this.getModularRotation(sceneObject);
			return newRotation ? true : false;
		}
		if (this.oneModuleOnScene()) {
			// Makes no sense to rotate one module on scene
			return false;
		}
		const isLocked =
			!this.isModularSofa() && this.moduleConnectionManager.hasConnectedModules(sceneObject);
		if (parentId || isLocked) {
			// Can not rotate child items
			return false;
		}
		let rotatable = true;
		const allChildren = this.connectionManager.getAllChildren(sceneObject.id);
		allChildren.forEach((c) => {
			const object = this.sceneItems.get(c);
			if (object && this.objectManager.legsManager.objects.includes(object)) {
				return;
			}
			if (
				(object && !this.connectionManager.isAddAsChild(object.articleId)) ||
				(object && this.connectionManager.isSeparate(object.articleId))
			) {
				// Can not rotate object if it has children that are not child only .
				rotatable = false;
			}
		});
		if (!rotatable) {
			return false;
		}
		const rotation = this.getNextRotation(sceneObject);
		return rotation ? true : false;
	}

	/**
	 * Rotate a scene object.
	 *
	 * @param sceneObject Scene Object.
	 */
	public rotate(sceneObject: SceneObject): boolean {
		if (!this.isRotatable(sceneObject)) {
			return false;
		}

		if (!this.props.rangeSettings.modularRotation) {
			const newRotation = this.getNextRotation(sceneObject);
			if (newRotation) {
				// To avoid 360°+ rotations
				if ((newRotation.y as number) > 360 * (Math.PI / 180)) {
					(newRotation.y as number) -= 360 * (Math.PI / 180);
				}
				// Apply new rotation
				sceneObject.transform.rotation = newRotation;
				if (sceneObject.node) {
					sceneObject.node.setRotation(newRotation);
				}
			}
		} else {
			const modularRotation = this.getModularRotation(sceneObject);
			if (modularRotation) {
				const childStandaloneObjects =
					this.connectionManager.getChildStandaloneObjects(sceneObject);
				// disconnect
				this.moduleConnectionManager.disconnect(sceneObject);
				childStandaloneObjects.forEach((childObject: SceneObject) => {
					this.moduleConnectionManager.disconnect(childObject);
				});

				if (modularRotation.connection) {
					const childStandaloneObjects =
						this.connectionManager.getChildStandaloneObjects(sceneObject);
					// disconnect
					this.moduleConnectionManager.disconnect(sceneObject);
					childStandaloneObjects.forEach((childObject: SceneObject) => {
						this.moduleConnectionManager.disconnect(childObject);
					});

					// apply new connection
					const parent = this.sceneItems.get(modularRotation.connection.parentId);
					if (parent) {
						this.moduleConnectionManager.connect(
							sceneObject,
							parent,
							modularRotation.connection.relation
						);
					}
				} else {
					// apply new rotation
					if (sceneObject && sceneObject.node) {
						sceneObject.transform.rotation = modularRotation.rotation;
						sceneObject.node.setRotation(modularRotation.rotation);
					}
				}
			}
		}
		return true;
	}

	/**
	 * Smart Rotate all scene objects.
	 *
	 */
	public smartRotateSceneItems(): void {
		this.sceneItems.forEach((sceneItem) => {
			if (this.isSmartRotateItem(sceneItem.articleId)) {
				this.smartRotateItem(sceneItem);
			}
		});
	}

	/**
	 * Smart Rotate a scene object.
	 *
	 * @param sceneItem Scene Object.
	 */
	public smartRotateItem(sceneItem: SceneObject): void {
		const currentRotation = this.connectionManager.getWorldRotation(sceneItem);
		if (sceneItem && sceneItem.node) {
			const connected = this.getCollidingObjects(sceneItem);

			// No need for rotation, object has no connections
			if (connected.length === 0) {
				return;
			}

			const unavailableRotations = this.getUnavailableRotations(sceneItem, connected);
			const childs = this.connectionManager.getChildObjects(sceneItem);

			const currentRotationY = this.getNormalizedDegRotation(currentRotation.y as number);
			if (!unavailableRotations.includes(currentRotationY)) {
				// No need for rotation, object rotation is valid
				return;
			}

			const possibleRotations = [90, -90, 180, 0];
			const newRotation = possibleRotations.find(
				(rotation) => !unavailableRotations.includes(rotation)
			);

			if (newRotation == null) {
				// has no valid rotations
				return;
			}

			this.moduleConnectionManager.disconnect(sceneItem);
			childs.forEach((child) => this.moduleConnectionManager.disconnect(child));

			// Rotate to first angle that is not occupied if 2 or more connections are occupied
			if (connected.length > 1) {
				const radians = newRotation * (Math.PI / 180);
				sceneItem.transform.rotation = { x: 0, y: radians, z: 0 };
				sceneItem.node.setRotation({ x: 0, y: radians, z: 0 });
			}
			// Only one side occupied -> rotate to open at opposite side of connected module
			else {
				const rotation = unavailableRotations[0];
				const reverseRotation = this.getOppositeDegreeOf(rotation) * (Math.PI / 180);
				sceneItem.transform.rotation = { x: 0, y: reverseRotation, z: 0 };
				sceneItem.node.setRotation({ x: 0, y: reverseRotation, z: 0 });
			}
			childs.forEach((child) => {
				if (!this.connectionManager.isStandalone(child)) {
					const allPoints = this.findClosestConnectionPoints(child);
					if (allPoints && allPoints[0].connection) {
						const newConnection = allPoints[0].connection.relation;
						this.moduleConnectionManager.connect(child, sceneItem, newConnection);
					}
				}
			});
		} else {
			throw 'SceneContainer: smartRotateItem(): sceneItem does not exist';
		}
	}

	/**
	 * Destroys all scene objects.
	 *
	 */
	public clearScene(): void {
		this.objectManager.automaticallyAddManager.remove(
			this.props.rangeSettings.rangeName !== 'JÄTTEBO'
		);
		this.sceneItems.forEach((sceneItem) => this.objectManager.destroyObject(sceneItem));
		this.connectionManager.clearConnections();
		this.moduleConnectionManager.clearConnections();
	}

	/**
	 * Gets min and max position extends of items on scene. With extended models and with normal ones.
	 *
	 * @param objects Scene Objects Map.
	 * @param ignoreAutoObjects Ignore armrests in calculations.
	 */
	public getPositionsExtends(
		objects: Map<string, SceneObject>,
		ignoreAutoObjects = true
	): { [key: string]: { min: TransformPosition; max: TransformPosition } } {
		const report = new Object() as {
			[key: string]: { min: TransformPosition; max: TransformPosition };
		};
		const min = new Object() as TransformPosition;
		const max = new Object() as TransformPosition;
		const bounds = ['default', 'extended'];

		bounds.forEach((b) => {
			objects.forEach((object) => {
				if (ignoreAutoObjects && this.connectionManager.isAutoConnectableModel(object.articleId)) {
					return;
				}
				const model = this.connectionManager.getModelData(object.articleId);
				if (!model || !model.bounds[b]) {
					return;
				}
				const rotation: TransformRotation = this.connectionManager.getWorldRotation(object);
				const position: TransformPosition = this.connectionManager.getWorldPosition(object);

				const size = model.bounds[b].size;

				const posOffset = TransformVector.rotate(
					model.bounds[b].position as TransformPosition,
					rotation
				);
				const modPosition = TransformVector.add(position, posOffset);

				const sizeOffsets = { x: size.width / 2, y: size.height / 2, z: size.depth / 2 };
				const rotatedOffset = TransformVector.rotate(sizeOffsets, rotation);

				const sub = TransformVector.sub(modPosition, rotatedOffset);
				const add = TransformVector.add(modPosition, rotatedOffset);

				if (min.x == undefined || min.x > Math.min(sub.x as number, add.x as number)) {
					min.x = Math.min(sub.x as number, add.x as number);
				}
				if (min.y == undefined || min.y > Math.min(sub.y as number, add.y as number)) {
					min.y = Math.min(sub.y as number, add.y as number);
				}
				if (min.z == undefined || min.z > Math.min(sub.z as number, add.z as number)) {
					min.z = Math.min(sub.z as number, add.z as number);
				}
				if (max.x == undefined || max.x < Math.max(sub.x as number, add.x as number)) {
					max.x = Math.max(sub.x as number, add.x as number);
				}
				if (max.y == undefined || max.y < Math.max(sub.y as number, add.y as number)) {
					max.y = Math.max(sub.y as number, add.y as number);
				}
				if (max.z == undefined || max.z < Math.max(sub.z as number, add.z as number)) {
					max.z = Math.max(sub.z as number, add.z as number);
				}
			});
			report[b] = {
				min: TransformVector.clone(min),
				max: TransformVector.clone(max),
			};
		});
		return report;
	}

	/**
	 * Checks if articles can be places on scene.
	 * Used in swiper.
	 *
	 * @param articleIds Array of articles.
	 */
	public getArticlesPlaceableStatuses(articleIds: string[]): { [key: string]: boolean } {
		const articlePlaceable = {};
		const items = this.getCurrentItemsWithoutAutoObjects();
		const gridBoxes = this.gridTools.createGridBoxes(items);
		articleIds.forEach((articleId) => {
			if (this.isArticlePlaceable(articleId)) {
				articlePlaceable[articleId] = true;
				return;
			}

			// Disable armrest for Jattebo if backrest is moved
			const model = this.connectionManager.getModelData(articleId);
			const hasSpecificItems = this.props.specificItems && this.props.specificItems.size > 0;
			if (model && model.modelId === 'JATTEBO_ARMREST' && hasSpecificItems) {
				articlePlaceable[articleId] = false;
				return;
			}

			const artIds = [articleId];
			if (
				this.smartMirrorManager.isSmartMirrorArticle(
					articleId,
					this.props.rangeSettings.smartMirrorModules
				)
			) {
				artIds.push(this.smartMirrorManager.getMirrorArticleWithArticleId(articleId));
			}
			articlePlaceable[articleId] = artIds.some((articleId) =>
				this.hasArticleConnectionPoints({ articleId, items, gridBoxes })
			);
		});
		return articlePlaceable;
	}

	/**
	 * Checks if article can be placed on scene.
	 *
	 * @param options Article placeable status options.
	 * @param options.articleId Article Identity.
	 * @param options.isSmartMirror Is it smart mirror check?
	 * @param options.keepAutoConnect Should it not consider removing auto connect?
	 * @param options.ignore Should it skip a node?
	 */
	public getArticlePlaceableStatus(options: {
		articleId: string;
		isSmartMirror?: boolean;
		keepAutoConnect?: boolean;
		ignore?: string[];
	}): boolean {
		if (this.isArticlePlaceable(options.articleId)) {
			return true;
		}
		const items = this.getCurrentItemsWithoutAutoObjects(options);
		const gridBoxes = this.gridTools.createGridBoxes(items);
		return this.hasArticleConnectionPoints({ ...options, items, gridBoxes });
	}

	/**
	 * Returns all unfinished sections.
	 *
	 */
	public getUnfinishedSections(): SceneObject[] {
		const sectionsThatCanBeUnfinished = this.props.rangeSettings.unfinishedModules || [];
		const unfinishedSections: SceneObject[] = [];
		this.sceneItems.forEach((sceneItem) => {
			const sceneItemFamily = this.connectionManager.getFamily(sceneItem.articleId);
			const children = this.connectionManager.getChildren(sceneItem.id).filter((childId) => {
				// A headrest can't make a section "finished"
				const child = this.sceneItems.get(childId);
				if (child) {
					return this.connectionManager.getFamily(child.articleId) !== 'HEADREST';
				}
				return false;
			});
			const connectedModules = this.moduleConnectionManager.getConnectedModules(sceneItem);
			const nrOfConnectedSections = connectedModules.length + children.length;

			if (
				sectionsThatCanBeUnfinished.includes(sceneItemFamily as FamilyType) &&
				nrOfConnectedSections < 2
			) {
				unfinishedSections.push(sceneItem);
			}
		});

		return unfinishedSections;
	}

	/**
	 * Returns all unfinished sections.
	 *
	 */
	public getStandaloneSofabed(): SceneObject[] {
		const standaloneSofabed: SceneObject[] = [];

		if (this.props.rangeSettings.rangeName !== 'KIVIK') {
			return standaloneSofabed;
		}

		this.sceneItems.forEach((sceneItem) => {
			const sceneItemFamily = this.connectionManager.getFamily(sceneItem.articleId);

			if (sceneItemFamily === 'SOFABED') {
				standaloneSofabed.push(sceneItem);
			}
		});

		return standaloneSofabed;
	}

	/**
	 * Returns current sofa configuration.
	 *
	 */
	public getSofaConfiguration = (coverOnly?: boolean): SofaConfiguration => {
		const sofaConfiguration: SofaConfiguration = {
			configuration: {
				children: [],
			},
		};
		this.sceneItems.forEach((sceneItem) => {
			/* Always add footstools to configuration */
			if (
				sceneItem.articleId == 'leg' ||
				(this.connectionManager.isAddAsChild(sceneItem.articleId) &&
					!this.connectionManager.isSeparate(sceneItem.articleId))
			) {
				return;
			}

			const sceneItemModelData = this.connectionManager.getModelData(sceneItem.articleId);
			if (
				sceneItemModelData &&
				this.objectManager.automaticallyAddManager.isAddAutomatically(sceneItemModelData.modelId)
			) {
				return;
			}

			const rotation = this.connectionManager.getWorldRotation(sceneItem);
			const position = this.connectionManager.getWorldPosition(sceneItem);
			const configurationChild = {
				articleId: sceneItem.articleId,
				children: this.getChildrenArray(sceneItem),
				pos: {
					x: (position.x as number) * 10,
					y: (position.y as number) * 10,
					z: -((position.z as number) * 10),
				},
				rot: {
					ry: (rotation.y as number) * (180 / Math.PI),
				},
			};
			sofaConfiguration.configuration.children.push(configurationChild);
		});

		if (coverOnly) {
			sofaConfiguration.coverOnly = true;
		}
		return sofaConfiguration;
	};

	/**
	 * Unfinished section placeable status.
	 *
	 */
	public getUnfinishedSectionPlaceableStatus(unfinishedSceneObject: SceneObject): {
		[key: string]: boolean;
	} {
		const unfinishedSectionPlaceable = {};
		const unfinishedItem = new Map<string, SceneObject>();
		unfinishedItem.set(String(unfinishedSceneObject.id), unfinishedSceneObject as SceneObject);
		// get current items
		const items = new Map(this.sceneItems);
		// get current grid boxes
		const gridBoxes = this.gridTools.createGridBoxes(items);

		const connectionManager = this.connectionManager;
		this.props.moduleItems
			.filter(function (module) {
				const family = connectionManager.getFamily(module.id);
				return family !== 'HEADREST'; // Headrests can't make a module "finished"
			})
			.forEach((item) => {
				const { id: articleId } = item;

				const connectableObject = {
					id: 'unfinishedSectionItem',
					articleId,
					transform: {
						position: TransformVector.zero(),
						rotation: TransformVector.zero(),
					},
				} as ConnectableObject;
				const connectionPoints = this.snappingGrid.calculateConnectionPoints(
					connectableObject,
					unfinishedItem,
					gridBoxes,
					this.props.rangeSettings.simpleConnectionKitsLogic
				);
				const hasValidConnections = connectionPoints.some(
					(p) => p.isValid && !p.intersections.length
				);
				unfinishedSectionPlaceable[articleId] = hasValidConnections;
			});
		return unfinishedSectionPlaceable;
	}

	/**
	 * Returns all missing cover sections.
	 *
	 */
	public getMissingCoverSections(): SceneObject[] {
		const missingCoverItems: SceneObject[] = [];
		const selectedCover = this.props.cover;

		if (this.props.rangeSettings.shouldColorAllModules) {
			this.sceneItems.forEach((sceneItem) => {
				if (sceneItem.articleId == 'leg') {
					return;
				}
				const sceneItemCover = ProductUtils.getColorIdFromProduct(
					this.props.products,
					sceneItem.articleId
				);
				if (sceneItemCover !== selectedCover.coverName) {
					missingCoverItems.push(sceneItem);
				}
			});
		}

		return missingCoverItems;
	}

	/* Private methods. */
	private InitiateManagers(): void {
		const { sofaGroups, products, sofaModels, rangeSettings } = this.props;

		this.connectionManager = new ConnectionManager(
			this.sceneItems,
			products,
			sofaGroups,
			sofaModels
		);
		this.gridTools = new GridTools(this.sceneItems, this.connectionManager);

		this.moduleConnectionManager = new ModuleConnectionManager(
			this.connectionManager,
			this.sceneItems,
			this.gridTools,
			this.isModularSofa()
		);

		this.connectionKitManager = new ConnectionKitsManager(
			this.connectionManager,
			this.moduleConnectionManager,
			this.sceneItems
		);

		this.objectManager = new ObjectManager(
			this.view3d,
			this.moduleConnectionManager,
			this.connectionKitManager,
			this.gridTools,
			this.props
		);

		this.snappingGrid = new SnappingGrid(
			this.connectionManager,
			this.moduleConnectionManager,
			this.connectionKitManager,
			this.sceneItems,
			this.getAutoObjectsArticles(),
			this.isModularSofa()
		);

		this.smartMirrorManager = new SmartMirrorManager(
			this.moduleConnectionManager,
			this.sceneItems,
			this.gridTools,
			products,
			rangeSettings
		);
	}

	private isAllModulesMiddleModuleDrag = (): boolean => {
		let isAll = true;
		this.sceneItems.forEach((item) => {
			const conn = this.moduleConnectionManager.getAllConnectedModules(item.id);
			if (conn && conn.length === 1) {
				isAll = false;
			}
		});
		return isAll;
	};

	private getChildrenArray = (sceneItem: SceneObject): SofaConfigurationItem[] => {
		const childArray: SofaConfigurationItem[] = [];
		/* Never add footstools as children */
		const children = this.connectionManager
			.getChildNotStandaloneObjects(sceneItem)
			.filter((child) => child.articleId !== 'leg')
			.filter((child) => !this.connectionManager.isSeparate(child.articleId));
		children.forEach((child) => {
			const childWorldPosition = this.connectionManager.getWorldPosition(child);
			const childWorldRotation = this.connectionManager.getWorldRotation(child);
			const childItem = {
				articleId: child.articleId,
				children: this.getChildrenArray(child),
				pos: {
					x: (childWorldPosition.x as number) * 10,
					y: (childWorldPosition.y as number) * 10,
					z: -((childWorldPosition.z as number) * 10),
				},
				rot: {
					ry: (childWorldRotation.y as number) * (180 / Math.PI),
				},
			};
			childArray.push(childItem);
		});
		return childArray;
	};

	private getCollidingObjects(sceneObject: SceneObject): Array<SceneObject> {
		const gridBoxes = this.gridTools.createGridBoxes(this.sceneItems, -0.05, [sceneObject.id]);
		const itemGridBox = this.gridTools.createGridBoxFromObject(sceneObject, 'default', -0.1);

		const intersections = this.gridTools.checkBoxIntersections(itemGridBox, gridBoxes);
		const collidingObjects = new Array<SceneObject>();
		const sceneItems = this.sceneItems;
		intersections.forEach(function (id) {
			const sceneItem = sceneItems.get(id);
			if (sceneItem) {
				collidingObjects.push(sceneItem);
			}
		});
		return collidingObjects;
	}

	private getUnavailableRotations(
		parent: ConnectableObject,
		childs: ConnectableObject[]
	): number[] {
		const unavailableRotations: number[] = [];
		childs.forEach((child) => {
			const parentPosition = this.connectionManager.getWorldPosition(parent);
			const childPosition = this.connectionManager.getWorldPosition(child);

			const diffX = (childPosition.x as number) - (parentPosition.x as number);
			const diffZ = (childPosition.z as number) - (parentPosition.z as number);
			const b = Math.abs(diffX) > Math.abs(diffZ);

			const x = b ? diffX : 0;
			const z = b ? 0 : diffZ;
			const rot = Math.atan2(x, z);

			const normalized = this.getNormalizedDegRotation(rot);
			const mirrored = this.getMirroredRotation(normalized);
			unavailableRotations.push(normalized, mirrored);
		});

		return unavailableRotations;
	}

	private allSceneItemsSeparate(items: Map<string, SceneObject>): boolean {
		const allItemsSeparate = Array.from(items).every((item) => {
			const sceneItem = item[1];
			return this.connectionManager.isSeparate(sceneItem.articleId);
		});

		return allItemsSeparate;
	}

	public hasTwoSeparateSofas(items: Map<string, SceneObject>): boolean {
		let nrOfSofas = 0;
		let hasTwoSeparateSofas = false;
		const checkedSofas: string[] = [];
		Array.from(items).forEach((item) => {
			const sceneItem = item[1];
			const modelData = this.connectionManager.getModelData(sceneItem.articleId);
			if (
				modelData &&
				!(
					modelData.modelId.includes('leg') ||
					modelData.modelId.includes('footstool') ||
					modelData.modelId.includes('armrest')
				) &&
				!hasTwoSeparateSofas
			) {
				const isConnected = this.moduleConnectionManager.getAllConnectedModules(sceneItem.id);
				if (isConnected.length < 2 && !checkedSofas.includes(sceneItem.id)) {
					nrOfSofas++;
					if (isConnected.length > 0) {
						checkedSofas.push(isConnected[0].object.id);
					}
					if (nrOfSofas > 1) {
						hasTwoSeparateSofas = true;
					}
				}
			}
		});

		return hasTwoSeparateSofas;
	}

	private isSmartRotateItem(sceneItemId: string): boolean {
		const sceneItemModel = this.connectionManager.getModelData(sceneItemId);
		const smartRotateModules = this.props.rangeSettings.smartRotateModules as string[];

		return sceneItemModel && smartRotateModules.includes(sceneItemModel.modelId);
	}

	private getNextRotation(sceneObject: SceneObject): TransformRotation | null {
		const { articleId, transform } = sceneObject;
		const children = this.connectionManager.getChildren(sceneObject.id);
		const gridBoxes = this.gridTools.createGridBoxes(this.sceneItems, 0, [
			...children,
			sceneObject.id,
		]);

		const rotations = [90 * (Math.PI / 180), 180 * (Math.PI / 180), 270 * (Math.PI / 180)];
		const rotation = rotations.find((rotation) => {
			const newRotation = transform.rotation
				? TransformVector.add(transform.rotation, {
						x: 0,
						y: rotation,
						z: 0,
				  })
				: null;

			if (!newRotation) {
				return null;
			}
			const bounds = this.isExtended(articleId) ? 'extended' : 'default';
			const int = this.gridTools.checkIntersection(
				articleId,
				{
					position: transform.position,
					rotation: newRotation,
				},
				bounds,
				-0.5,
				gridBoxes
			);
			if (int.length) {
				// Will intersect item on grid.
				return false;
			}
			return true;
		});
		return rotation && transform.rotation
			? TransformVector.add(transform.rotation, {
					x: 0,
					y: rotation,
					z: 0,
			  })
			: null;
	}

	private getModularRotation(sceneObject: SceneObject): {
		rotation: TransformRotation;
		connection: { parentId: string; relation: ConnectionRelation };
	} | null {
		const childStandaloneObjects = this.connectionManager.getChildStandaloneObjects(sceneObject);
		const sceneObjectStandalone = !sceneObject.parentId && childStandaloneObjects.length === 0;
		const rotations = [90 * (Math.PI / 180), 180 * (Math.PI / 180), 270 * (Math.PI / 180)];
		const worldTransform = this.connectionManager.getWorldTransform(sceneObject);

		if (worldTransform && worldTransform.rotation) {
			let newRotation: TransformRotation | null = null;
			let newConnection: { parentId: string; relation: ConnectionRelation } | null = null;
			for (const rotation of rotations) {
				// apply rotation
				const testRotation = TransformVector.add(worldTransform.rotation, {
					x: 0,
					y: rotation,
					z: 0,
				});

				if (testRotation && testRotation.y) {
					// Try to connect to nearest connection points
					const normalizedTestRotation = this.getNormalizedDegRotation(testRotation.y);
					const mirroredTestRotation = this.getMirroredRotation(normalizedTestRotation);
					// Check if there is available snapping points with this rotation
					const allPoints = this.findClosestConnectionPoints(sceneObject);
					const points = allPoints.filter((point) => {
						if (point.transform && point.transform.rotation && point.transform.rotation.y) {
							const pointNormalized = this.getNormalizedDegRotation(point.transform.rotation.y);
							if (
								normalizedTestRotation == pointNormalized ||
								mirroredTestRotation == pointNormalized
							) {
								return true;
							}
							return false;
						}
						return false;
					});
					if (points.length && points[0].connection) {
						if (points.length == 1) {
							newRotation = testRotation;
							newConnection = points[0].connection;
							break;
						}
						// find closest one
						let distance = Number.MAX_SAFE_INTEGER;
						let closest: SnappingPoint | null = null;
						points.forEach((point) => {
							const pointTransform = point.transform;
							if (pointTransform && pointTransform.position) {
								const dist = TransformVector.distanceSquared(
									worldTransform.position as TransformPosition,
									pointTransform.position
								);
								if (dist < distance) {
									distance = dist;
									closest = point;
								}
							}
						});
						if (closest) {
							const close = closest as SnappingPoint;
							newRotation = testRotation;
							newConnection = close.connection as Connection;
						}
						break;
					}

					// if no connections - check if the new rotation intersects with the grid
					const childOnly = this.connectionManager.getAllChildOnlyChildren(sceneObject.id);
					const gridBoxes = this.gridTools.createGridBoxes(this.sceneItems, 0, [
						...childOnly,
						sceneObject.id,
					]);
					const bounds = this.isExtended(sceneObject.articleId) ? 'extended' : 'default';
					const int = this.gridTools.checkIntersection(
						sceneObject,
						{
							position: worldTransform.position,
							rotation: testRotation,
						},
						bounds,
						0.15,
						gridBoxes
					);

					if (!int.length) {
						newRotation = testRotation;
						if (sceneObjectStandalone) {
							break;
						}
					}
				}
			}
			if (newRotation || newConnection) {
				return {
					rotation: newRotation as TransformRotation,
					connection: newConnection as Connection,
				};
			}
		}
		return null;
	}

	private findClosestConnectionPoints(sceneObject: SceneObject): Array<SnappingPoint> {
		const worldPosition = this.connectionManager.getWorldPosition(sceneObject);
		const childOnly = this.connectionManager.getAllChildOnlyChildren(sceneObject.id);
		const gridBoxes = this.gridTools.createGridBoxes(this.sceneItems, 0.15, [
			sceneObject.id,
			...childOnly,
		]);
		const snappingPoints = this.snappingGrid.calculateConnectionPoints(
			sceneObject,
			this.sceneItems,
			gridBoxes,
			this.props.rangeSettings.simpleConnectionKitsLogic,
			true,
			false
		);
		return snappingPoints.filter((point) => {
			if (point && point.transform && point.transform.position) {
				if (!point.isValid || point.intersections.length) {
					return false;
				}
				const distance = TransformVector.distance(worldPosition, point.transform.position);
				if (distance > 4) {
					return false;
				}
			}
			return true;
		});
	}

	private getNormalizedDegRotation(rad: number): number {
		return Math.round(rad * (180 / Math.PI)) % 360;
	}

	private getMirroredRotation(deg: number): number {
		switch (deg) {
			case 0:
				return 360;
			case 90:
				return -270;
			case 180:
				return -180;
			case 270:
				return -90;
			case 360:
				return 0;
			case -90:
				return 270;
			case -180:
				return 180;
			case -270:
				return 90;
			default:
				return deg;
		}
	}

	private getOppositeDegreeOf(degree: number): number {
		switch (degree) {
			case 90:
			case -270:
				return -90;
			case -90:
			case 270:
				return 90;
			case 0:
			case 360:
				return 180;
			case 180:
			case -180:
				return 0;
			default:
				return degree;
		}
	}

	private getAutoObjectsArticles(): string[] {
		const articleId = this.objectManager.automaticallyAddManager.getArticle();
		return articleId ? [articleId] : [];
	}

	private getCurrentItemsWithoutAutoObjects(options?: {
		isSmartMirror?: boolean;
		keepAutoConnect?: boolean;
		ignore?: string[];
	}): Map<string, SceneObject> {
		// get current items with no auto objects
		const items = new Map(this.sceneItems);
		items.forEach((item, id) => {
			if (options?.ignore?.includes(id) || options?.ignore?.includes(item.parentId || '')) {
				items.delete(id);
			} else if (this.connectionManager.isAutoConnectableModel(item.articleId)) {
				if (!options?.keepAutoConnect && !this.connectionManager.isAutoConnectMovable(item)) {
					items.delete(id);
				}
			}
		});
		return items;
	}

	private isArticlePlaceable(articleId: string): boolean {
		const isStandalone = !this.connectionManager.isAddAsChild(articleId);
		// If scene is empty or only have separate items, only "standalone" models can be placed
		if (
			isStandalone &&
			(this.sceneItems.size === 0 || this.allSceneItemsSeparate(this.sceneItems))
		) {
			return true;
		}
		// "separate" models can always be placed
		if (this.connectionManager.isSeparate(articleId)) {
			return true;
		}

		// Lastly check if valid connections can be made
		if (!this.snappingGrid) {
			// is initial load
			return true;
		}
		return false;
	}

	private hasArticleConnectionPoints(options: {
		articleId: string;
		items: Map<string, SceneObject>;
		gridBoxes: Map<string, GridBox>;
		isSmartMirror?: boolean;
	}): boolean {
		return this.snappingGrid
			.calculateConnectionPoints(
				{
					id: 'fakeItem',
					articleId: options.articleId,
					transform: {
						position: TransformVector.zero(),
						rotation: TransformVector.zero(),
					},
				} as ConnectableObject,
				options.items,
				options.gridBoxes,
				this.props.rangeSettings.simpleConnectionKitsLogic,
				false,
				true,
				options.isSmartMirror
			)
			.some((p) => this.filterCalculatedConnectionPoints(p));
	}

	private filterCalculatedConnectionPoints(point: SnappingPoint) {
		return point.isValid && !point.intersections.length;
	}
}
